import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';

import useUserContext from 'ecommerce/context/user';

import PhotoGrid, { createGridItem, PHOTO_GRID_ACTION, PHOTO_GRID_PAGE_SIZE } from '../photos/photoGrid';
import ancestorsApi from '../../api/ancestors';
import {
  transformTreeMedia,
} from '../../api/ancestry';
import useEditorItemContext from '../../context/editorItemContext';

const pageSize = PHOTO_GRID_PAGE_SIZE;

const fetchMediaPage = async (type, accessToken, ancestryAuth, gridItems, itemCount, page, treeId) => {
  // Fetch one page of the media.
  const { data: pageMedia, error } = await ancestorsApi(type, accessToken, ancestryAuth).getTreeMedia(
    treeId,
    page,
    {
      pageSize,
    },
  );
  // If request fails, stop the infinite scrolling.
  if (error) {
    console.error(error);
    return { hasMore: false, page, gridItems };
  }
  // Request succeeded, increment page number for the next fetch.
  const newPage = page + 1;
  // Transform media data to photos format.
  const photos = transformTreeMedia(pageMedia);
  // Create new grid items from the fetched photos.
  const createdGridItems = photos.map(ancestryPhoto =>
    createGridItem({
      id: ancestryPhoto.id,
      label: ancestryPhoto.name,
      url: ancestryPhoto.url,
      thumbUrl: ancestryPhoto.thumbUrl,
      custom: { ancestryPhoto },
    }),
  );
  // Add the created items to the end of the infinite list.
  const newGridItems = [...gridItems, ...createdGridItems];
  // If there is less fetched media than requested, that means we are on the last page
  // and there are no more media to load.
  if (pageMedia.length < pageSize) {
    return { hasMore: false, page: newPage, gridItems: newGridItems };
  }
  return { hasMore: true, page: newPage, gridItems: newGridItems };
};

// TODO Don't fetch media until grid is visible.
// - In other 2 grid this is implemented, but here it would be a bit more difficult,
//   as repeated showing / hiding would trigger unwanted loading of next page.
// - Currently this functionality isn't needed, as this grid is at first rendered visible.
// TODO What to do if a fetch page request fails?
// - Add the retry button, which user must activate, to prevent spamming?
// - This problem is present in all the Ancestry grids.
const AncestryMediaGrid = ({ type, isVisible, onSelect, columnCount, selectedIds }) => {
  const editorItemContext = useEditorItemContext();
  const treeId = editorItemContext.item.additionalInfo?.treeId;
  const { accessToken, ancestryAuth } = useUserContext();

  // Both accessToken and treeId must be defined, so they can be used to fetch the media.
  // Without them, there is nothing to fetch and nothing to display, so empty message is displayed.
  const [hasMore, setHasMore] = useState(accessToken != null && treeId != null);
  const [isLoadingState, setIsLoadingState] = useState(false);
  const [itemCount, setItemCount] = useState(0);
  const [page, setPage] = useState(1);
  const [gridItems, setGridItems] = useState([]);
  const isLoadingRef = useRef(false);

  /**
   * Set both reference and state value.
   * Reference is used for immediate value, state is for rendering.
   * @param {boolean} value
   * @returns {undefined}
   */
  const setIsLoading = value => {
    isLoadingRef.current = value;
    setIsLoadingState(value);
  };

  const loadMore = ({ itemCount: newItemCount }) => {
    setItemCount(newItemCount);
    // Do nothing if another fetch is in progress, or there are no more items to fetch.
    if (isLoadingRef.current || !hasMore) {
      return;
    }
    // Prevent another invocation by setting the loading lock.
    setIsLoading(true);
    (async () => {
      // Fetch the new media.
      const newState = await fetchMediaPage(
        type,
        accessToken,
        ancestryAuth,
        gridItems,
        itemCount,
        page,
        treeId,
      );
      // Set the newly fetched values.
      setGridItems(newState.gridItems);
      setHasMore(newState.hasMore);
      setPage(newState.page);
      // Set the loading as the last thing, because it unlocks another calls.
      setIsLoading(false);
    })();
  };

  return (
    <PhotoGrid
      columnCount={columnCount}
      empty={<div className="text-center">Sorry, we couldn&apos;t find any photos.</div>}
      hasMore={hasMore}
      isVisible={isVisible}
      isLoading={isLoadingState}
      items={gridItems}
      loadMore={loadMore}
      onSelect={({ item: { custom }, previewWidth, previewHeight }) => onSelect(custom, previewWidth, previewHeight)}
      assetType="photo"
      actions={[PHOTO_GRID_ACTION.FAVORITE, PHOTO_GRID_ACTION.BACKGROUND]}
      selectedIds={selectedIds}
    />
  );
};

AncestryMediaGrid.defaultProps = {
  type: 'ancestry',
  isVisible: true,
  selectedIds: {},
  onSelect: () => { },
};

AncestryMediaGrid.propTypes = {
  type: PropTypes.string,
  columnCount: PropTypes.number,
  isVisible: PropTypes.bool,
  onSelect: PropTypes.func,
  selectedIds: PropTypes.object,
};

export default AncestryMediaGrid;
